import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { pdfjs } from 'react-pdf';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { PAGES } from '../../constants/pages';
import useKycVerification from '../../hooks/useKycVerification';
import KycLink from '../KycVerification/KycLink';
import BaseLayout from '../../components/BaseLayout';
import KycSelectionContainer from '../../components/KycSelectionContainer';
import Header from '../../components/Header';
import { getRequiredDocuments } from '../../store/actions/documents/documentsActions';
import useApplicationUid from '../../hooks/useApplicationUid';

import { selectRequiredDocuments } from '../../store/selectors/documentsSelectors';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useStepPost from '../../hooks/useStepPost';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { useApplicantData } from '../../hooks/useApplicantData';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const Wrapper = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 500px) {
    padding: 0;
  }
`;

const KycVerification = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ApplicantData = useApplicantData();
  const { onSuccess: onSuccessSkip } = useStepPost({
    nextPath: PAGES.KYC_DONE_SKIP,
  });
  const { onSuccess: onSuccessDone } = useStepPost({
    nextPath: PAGES.KYC_DONE,
  });
  const requiredDocuments = useSelector(selectRequiredDocuments);
  const { kycOptions } = useKycVerification(requiredDocuments);
  const { ApplicationUid } = useApplicationUid();
  const options = kycOptions();
  const { BrokerName } = useSelector(selectSettings);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc verification',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc verification',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
    });
  };

  useEffect(() => {
    setTimeout(() => {
      dispatch(
        getRequiredDocuments({
          ApplicationUid,
        }),
      );
    }, [1000]);
  }, [ApplicationUid, dispatch]);

  useEffect(() => {
    if (
      !!requiredDocuments.length &&
      requiredDocuments.every((document) => document.IsUploaded === true)
    ) {
      onSuccessDone();
    }
  }, [requiredDocuments]); // eslint-disable-line

  return (
    <BaseLayout>
      <Helmet>
        <title>
          {t('kycVerification.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Wrapper>
        <Header
          title={t('kycFail.title')}
          subtitle={t('kycFail.subtitle')}
          subTitleMb={24}
        />
        {options?.map((option) => (
          <KycSelectionContainer key={option.path} {...option} />
        ))}

        <KycLink
          onClick={() => {
            handleGtmEvent();
            onSuccessSkip();
          }}
        />
      </Wrapper>
    </BaseLayout>
  );
};

export default KycVerification;
